import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, BarChart3, Settings, ChevronLeft, Search, 
  Ban, CheckCircle, Trash2, Key, X, RefreshCw,
  UserPlus, ToggleLeft, ToggleRight, Loader2, Edit3,
  CreditCard, MessageCircle, Send, FileText, Image,
  Eye, Tag, DollarSign, Percent, Calendar, Gift, Menu, AlertTriangle, Play,
  Target
} from 'lucide-react';
import CRMPanel from './CRMPanel';
import AdminHelpdesk from './AdminHelpdesk';
import NewsletterPanel from './NewsletterPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  { value: 'free', label: 'Free' },
  { value: 'starter', label: 'Starter' },
  { value: 'pro', label: 'Pro' },
  { value: 'church', label: 'Church' },
  { value: 'organization', label: 'Organization' },
];

export default function AdminPanel({ onClose }) {
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === 'platform_admin';
  const [activeTab, setActiveTabRaw] = useState(() => sessionStorage.getItem('admin_tab') || 'users');
  const setActiveTab = (tab) => { setActiveTabRaw(tab); sessionStorage.setItem('admin_tab', tab); };
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  
  // Add User state
  const [showAddUser, setShowAddUser] = useState(false);
  const [addUserForm, setAddUserForm] = useState({ email: '', password: '', name: '', role: 'user', subscription_tier: 'free' });
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState('');

  // Edit User state
  const [showEditUser, setShowEditUser] = useState(false);
  const [editUserForm, setEditUserForm] = useState({ name: '', email: '', church_name: '', role: '', subscription_tier: '', is_active: true });
  const [editUserId, setEditUserId] = useState(null);

  // Settings state
  const [platformSettings, setPlatformSettings] = useState({});
  const [settingsForm, setSettingsForm] = useState({});
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsTestResult, setSettingsTestResult] = useState(null);
  
  // Module config state
  const [moduleConfig, setModuleConfig] = useState({});
  const [moduleSaving, setModuleSaving] = useState(false);
  
  // Payments state
  const [payments, setPayments] = useState([]);
  
  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketResponse, setTicketResponse] = useState('');
  
  // CMS state
  const [aboutContent, setAboutContent] = useState(null);
  const [aboutImageUrl, setAboutImageUrl] = useState('');
  const [cmsSaving, setCmsSaving] = useState(false);
  
  // Articles state
  const [articles, setArticles] = useState([]);
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [articleForm, setArticleForm] = useState({ title: '', content: '', category: 'general', published: true });
  
  // Pricing state (platform admin)
  const [pricingTiers, setPricingTiers] = useState({});
  const [editingTier, setEditingTier] = useState(null);
  const [tierEditPrice, setTierEditPrice] = useState('');
  
  // Promo state (platform admin)
  const [promos, setPromos] = useState([]);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [promoForm, setPromoForm] = useState({ code: '', discount_type: 'percentage', discount_value: '', max_uses: -1, expires_at: '' });
  
  // Discount state
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountUser, setDiscountUser] = useState(null);
  const [discountForm, setDiscountForm] = useState({ discount_type: 'percentage', discount_value: '', reason: '' });

  // Bulk selection state
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // LLM Budget state
  const [budgetChecking, setBudgetChecking] = useState(false);
  const [budgetData, setBudgetData] = useState(null);

  // Revenue & Org state
  const [revenueData, setRevenueData] = useState(null);

  // Guide CMS state
  const [guideVideos, setGuideVideos] = useState([]);
  const [newGuideVideoTitle, setNewGuideVideoTitle] = useState('');
  const [newGuideVideoUrl, setNewGuideVideoUrl] = useState('');
  const [newGuideVideoSection, setNewGuideVideoSection] = useState('');
  const [newGuideVideoDesc, setNewGuideVideoDesc] = useState('');

  // Per-user module override
  const [userModuleOverrides, setUserModuleOverrides] = useState({});

  // Custom plan requests
  const [planRequests, setPlanRequests] = useState([]);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'users') {
        const response = await axios.get(`${API}/admin/users`);
        setUsers(response.data.users);
      } else if (activeTab === 'analytics') {
        const [analyticsRes, revenueRes] = await Promise.all([
          axios.get(`${API}/admin/analytics`),
          axios.get(`${API}/admin/revenue`).catch(() => ({ data: null }))
        ]);
        setAnalytics(analyticsRes.data);
        if (revenueRes.data) setRevenueData(revenueRes.data);
      } else if (activeTab === 'modules') {
        const response = await axios.get(`${API}/admin/modules`);
        setModuleConfig(response.data.modules);
      } else if (activeTab === 'payments') {
        const response = await axios.get(`${API}/admin/payments`);
        setPayments(response.data);
      } else if (activeTab === 'tickets') {
        const response = await axios.get(`${API}/admin/tickets`);
        setTickets(response.data.tickets);
      } else if (activeTab === 'content') {
        const [cmsRes, artRes, guideRes, planReqRes] = await Promise.all([
          axios.get(`${API}/cms/about`),
          axios.get(`${API}/articles?published_only=false`),
          axios.get(`${API}/guide/content`).catch(() => ({ data: { videos: [] } })),
          isPlatformAdmin ? axios.get(`${API}/admin/plan-requests`).catch(() => ({ data: { requests: [] } })) : Promise.resolve({ data: { requests: [] } })
        ]);
        setAboutContent(cmsRes.data.content);
        setAboutImageUrl(cmsRes.data.content?.image_url || '');
        setArticles(artRes.data.articles);
        setGuideVideos(guideRes.data.videos || []);
        setPlanRequests(planReqRes.data.requests || []);
      } else if (activeTab === 'pricing' && isPlatformAdmin) {
        const res = await axios.get(`${API}/admin/pricing`);
        setPricingTiers(res.data.tiers);
      } else if (activeTab === 'promos' && isPlatformAdmin) {
        const res = await axios.get(`${API}/admin/promos`);
        setPromos(res.data.promos);
      } else if (activeTab === 'budget' && isPlatformAdmin) {
        const res = await axios.get(`${API}/admin/llm-budget`);
        setBudgetData(res.data);
      } else if (activeTab === 'settings' && isPlatformAdmin) {
        const res = await axios.get(`${API}/admin/settings`);
        setPlatformSettings(res.data.settings);
        // Init form with masked values
        const form = {};
        for (const [key, val] of Object.entries(res.data.settings)) {
          form[key] = val.masked || '';
        }
        setSettingsForm(form);
      }
    } catch (err) {
      console.error('Failed to load admin data:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, 
        { is_active: !currentStatus }
      );
      loadData();
    } catch (err) {
      console.error('Failed to update user:', err);
    }
  };

  const openEditUser = (u) => {
    setEditUserId(u.id);
    setEditUserForm({
      name: u.name || '',
      email: u.email || '',
      church_name: u.church_name || '',
      role: u.role || 'user',
      subscription_tier: u.subscription_tier || 'free',
      is_active: u.is_active !== false,
    });
    setShowEditUser(true);
  };

  const handleEditUser = async () => {
    if (!editUserId) return;
    try {
      await axios.put(`${API}/admin/users/${editUserId}`, editUserForm);
      setShowEditUser(false);
      setEditUserId(null);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      loadData();
      setSelectedUser(null);
    } catch (err) {
      console.error('Failed to delete user:', err);
    }
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.post(`${API}/admin/users/${selectedUser.id}/reset-password?new_password=${encodeURIComponent(newPassword)}`, {});
      setShowResetModal(false);
      setNewPassword('');
      alert('Password reset successfully');
    } catch (err) {
      console.error('Failed to reset password:', err);
    }
  };

  const handleAddUser = async () => {
    setAddUserError('');
    if (!addUserForm.email || !addUserForm.password || !addUserForm.name) {
      setAddUserError('All fields are required');
      return;
    }
    if (addUserForm.password.length < 6) {
      setAddUserError('Password must be at least 6 characters');
      return;
    }
    setAddUserLoading(true);
    try {
      await axios.post(`${API}/admin/users/create`, addUserForm);
      setShowAddUser(false);
      setAddUserForm({ email: '', password: '', name: '', role: 'user', subscription_tier: 'free' });
      loadData();
    } catch (err) {
      setAddUserError(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleModuleToggle = async (moduleId) => {
    const updated = { ...moduleConfig };
    updated[moduleId] = { ...updated[moduleId], enabled: !updated[moduleId].enabled };
    setModuleConfig(updated);
    setModuleSaving(true);
    try {
      const toggleMap = {};
      for (const [key, val] of Object.entries(updated)) {
        toggleMap[key] = val.enabled;
      }
      await axios.put(`${API}/admin/modules`, { modules: toggleMap });
    } catch (err) {
      console.error('Failed to update modules:', err);
      loadData();
    } finally {
      setModuleSaving(false);
    }
  };

  const handleTicketResponse = async () => {
    if (!ticketResponse.trim() || !selectedTicket) return;
    try {
      await axios.put(`${API}/admin/tickets/${selectedTicket.id}`, { response: ticketResponse, status: 'responded' });
      setTicketResponse('');
      setSelectedTicket(null);
      loadData();
    } catch (err) {
      console.error('Failed to respond to ticket:', err);
    }
  };

  const handleCloseTicket = async (ticketId) => {
    try {
      await axios.put(`${API}/admin/tickets/${ticketId}`, { status: 'closed' });
      loadData();
    } catch (err) {
      console.error('Failed to close ticket:', err);
    }
  };

  const handleSaveAboutImage = async () => {
    setCmsSaving(true);
    try {
      const updatedContent = { ...aboutContent, image_url: aboutImageUrl };
      await axios.put(`${API}/admin/cms/about`, { content: updatedContent });
      setAboutContent(updatedContent);
    } catch (err) {
      console.error('Failed to save about page:', err);
    } finally {
      setCmsSaving(false);
    }
  };

  const handleCreateArticle = async () => {
    if (!articleForm.title || !articleForm.content) return;
    try {
      await axios.post(`${API}/admin/articles`, articleForm);
      setShowArticleForm(false);
      setArticleForm({ title: '', content: '', category: 'general', published: true });
      loadData();
    } catch (err) {
      console.error('Failed to create article:', err);
    }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await axios.delete(`${API}/admin/articles/${id}`);
      loadData();
    } catch (err) {
      console.error('Failed to delete article:', err);
    }
  };

  const handleImpersonate = async (userId) => {
    try {
      const res = await axios.post(`${API}/admin/impersonate/${userId}`, {});
      if (res.data.access_token) {
        localStorage.setItem('access_token', res.data.access_token);
      }
      window.location.href = '/app';
    } catch (err) {
      console.error('Impersonation failed:', err);
      alert(err.response?.data?.detail || 'Impersonation failed');
    }
  };

  const handleSavePricing = async (tierKey) => {
    try {
      await axios.put(`${API}/admin/pricing`, { tier: tierKey, price: parseFloat(tierEditPrice) });
      setEditingTier(null);
      loadData();
    } catch (err) {
      console.error('Failed to update pricing:', err);
    }
  };

  const handleCreatePromo = async () => {
    if (!promoForm.code || !promoForm.discount_value) return;
    try {
      await axios.post(`${API}/admin/promos`, {
        ...promoForm,
        discount_value: parseFloat(promoForm.discount_value),
        max_uses: promoForm.max_uses ? parseInt(promoForm.max_uses) : -1,
      });
      setShowPromoForm(false);
      setPromoForm({ code: '', discount_type: 'percentage', discount_value: '', max_uses: -1, expires_at: '' });
      loadData();
    } catch (err) {
      console.error('Failed to create promo:', err);
      alert(err.response?.data?.detail || 'Failed to create promo');
    }
  };

  const handleDeletePromo = async (id) => {
    if (!window.confirm('Delete this promo code?')) return;
    try {
      await axios.delete(`${API}/admin/promos/${id}`);
      loadData();
    } catch (err) {
      console.error('Failed to delete promo:', err);
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountUser || !discountForm.discount_value) return;
    try {
      await axios.post(`${API}/admin/users/${discountUser.id}/discount`, {
        user_id: discountUser.id,
        discount_type: discountForm.discount_type,
        discount_value: parseFloat(discountForm.discount_value),
        reason: discountForm.reason
      });
      setShowDiscountModal(false);
      setDiscountUser(null);
      setDiscountForm({ discount_type: 'percentage', discount_value: '', reason: '' });
      alert('Discount applied successfully');
    } catch (err) {
      console.error('Failed to apply discount:', err);
    }
  };

  const handleCheckBudget = async () => {
    setBudgetChecking(true);
    try {
      const res = await axios.post(`${API}/admin/llm-budget/check`);
      const data = res.data;
      setBudgetData(data);
      if (data.status === 'exceeded') {
        alert(`Budget Exceeded!\nCurrent cost: $${data.current_cost?.toFixed(2) || '?'}\nMax budget: $${data.max_budget?.toFixed(2) || '?'}\n\nPlease add balance via Profile → Universal Key → Add Balance`);
      } else {
        alert('AI budget is active and working!');
      }
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to check budget');
    } finally {
      setBudgetChecking(false);
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleSelectUser = (userId) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUserIds.size === filteredUsers.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(filteredUsers.map(u => u.id)));
    }
  };

  const handleBulkAction = async (action, value) => {
    if (selectedUserIds.size === 0) return;
    const label = action === 'delete' ? 'delete' : action === 'deactivate' ? 'deactivate' : action;
    if (!window.confirm(`Are you sure you want to ${label} ${selectedUserIds.size} user(s)?`)) return;
    setBulkActionLoading(true);
    try {
      const res = await axios.post(`${API}/admin/users/bulk-action`, {
        user_ids: Array.from(selectedUserIds),
        action,
        value,
      });
      alert(res.data.message);
      setSelectedUserIds(new Set());
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkActionLoading(false);
    }
  };

  if (user?.role !== 'admin' && user?.role !== 'platform_admin') return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex flex-col md:flex-row" data-testid="admin-panel">
      {/* Mobile top bar */}
      <div className="md:hidden bg-[#1B3A6B] px-4 py-3 flex items-center justify-between">
        <button onClick={onClose} className="flex items-center gap-2 text-white/70 hover:text-white text-sm">
          <ChevronLeft className="w-5 h-5" /> Back
        </button>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white/70 hover:text-white" data-testid="admin-menu-toggle">
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'block' : 'hidden'} md:block w-full md:w-64 bg-[#1B3A6B] text-white flex-shrink-0 md:flex md:flex-col`}>
        <div className="hidden md:block p-4 border-b border-white/10">
          <button onClick={onClose} className="flex items-center gap-2 text-white/70 hover:text-white" data-testid="admin-back-btn">
            <ChevronLeft className="w-5 h-5" />
            Back to App
          </button>
        </div>
        
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <button onClick={() => { setActiveTab('users'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'users' ? 'bg-white/20' : 'hover:bg-white/10'}`} data-testid="admin-tab-users">
            <Users className="w-5 h-5" />
            User Management
          </button>
          <button onClick={() => { setActiveTab('modules'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'modules' ? 'bg-white/20' : 'hover:bg-white/10'}`} data-testid="admin-tab-modules">
            <Settings className="w-5 h-5" />
            Module Toggles
          </button>
          <button onClick={() => { setActiveTab('tickets'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'tickets' ? 'bg-white/20' : 'hover:bg-white/10'}`} data-testid="admin-tab-tickets">
            <MessageCircle className="w-5 h-5" />
            Helpdesk
          </button>
          <button onClick={() => { setActiveTab('content'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'content' ? 'bg-white/20' : 'hover:bg-white/10'}`} data-testid="admin-tab-content">
            <FileText className="w-5 h-5" />
            Content / CMS
          </button>
          <button onClick={() => { setActiveTab('analytics'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'analytics' ? 'bg-white/20' : 'hover:bg-white/10'}`} data-testid="admin-tab-analytics">
            <BarChart3 className="w-5 h-5" />
            Analytics
          </button>
          {isPlatformAdmin && (
            <>
              <div className="border-t border-white/10 my-2 pt-2">
                <span className="text-xs text-white/40 px-4 uppercase tracking-wider">Platform Admin</span>
              </div>
              <button onClick={() => { setActiveTab('payments'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'payments' ? 'bg-white/20' : 'hover:bg-white/10'}`} data-testid="admin-tab-payments">
                <CreditCard className="w-5 h-5" />
                Payments & Stripe
              </button>
              <button onClick={() => { setActiveTab('crm'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'crm' ? 'bg-white/20' : 'hover:bg-white/10'}`} data-testid="admin-tab-crm">
                <Target className="w-5 h-5" />
                CRM & Leads
              </button>
              <button onClick={() => { setActiveTab('newsletter'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'newsletter' ? 'bg-white/20' : 'hover:bg-white/10'}`} data-testid="admin-tab-newsletter">
                <Send className="w-5 h-5" />
                Newsletters
              </button>
              <button onClick={() => { setActiveTab('pricing'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'pricing' ? 'bg-white/20' : 'hover:bg-white/10'}`} data-testid="admin-tab-pricing">
                <DollarSign className="w-5 h-5" />
                Pricing Editor
              </button>
              <button onClick={() => { setActiveTab('promos'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'promos' ? 'bg-white/20' : 'hover:bg-white/10'}`} data-testid="admin-tab-promos">
                <Tag className="w-5 h-5" />
                Promo Codes
              </button>
              <button onClick={() => { setActiveTab('budget'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'budget' ? 'bg-white/20' : 'hover:bg-white/10'}`} data-testid="admin-tab-budget">
                <DollarSign className="w-5 h-5" />
                AI Budget
              </button>
              <button onClick={() => { setActiveTab('settings'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'settings' ? 'bg-white/20' : 'hover:bg-white/10'}`} data-testid="admin-tab-settings">
                <Key className="w-5 h-5" />
                API Settings
              </button>
            </>
          )}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 bg-[#F9F8F6] overflow-y-auto">
        <div className="p-8">
          {/* USERS TAB */}
          {activeTab === 'users' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl font-bold text-[#1B3A6B]">User Management</h2>
                <div className="flex items-center gap-2">
                  <button onClick={() => setShowAddUser(true)} className="btn-primary py-2 px-4 rounded-lg font-medium flex items-center gap-2 text-sm" data-testid="add-user-btn">
                    <UserPlus className="w-4 h-4" />
                    Add User
                  </button>
                  <button onClick={loadData} className="btn-secondary p-2 rounded-lg">
                    <RefreshCw className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#475569]" />
                <input type="text" placeholder="Search users..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-white rounded-lg border-none shadow-sm focus:ring-2 focus:ring-[#1B3A6B]" data-testid="admin-user-search" />
              </div>

              {/* Bulk Actions Bar */}
              {selectedUserIds.size > 0 && (
                <div className="mb-4 bg-[#1B3A6B] text-white rounded-xl p-4 flex flex-wrap items-center gap-3 shadow-lg" data-testid="bulk-actions-bar">
                  <span className="text-sm font-medium">{selectedUserIds.size} user(s) selected</span>
                  <div className="flex flex-wrap items-center gap-2 ml-auto">
                    <button onClick={() => handleBulkAction('activate')} disabled={bulkActionLoading} className="px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded-lg text-xs font-medium transition-colors disabled:opacity-50" data-testid="bulk-activate-btn">
                      <CheckCircle className="w-3.5 h-3.5 inline mr-1" />Activate
                    </button>
                    <button onClick={() => handleBulkAction('deactivate')} disabled={bulkActionLoading} className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 rounded-lg text-xs font-medium transition-colors disabled:opacity-50" data-testid="bulk-deactivate-btn">
                      <Ban className="w-3.5 h-3.5 inline mr-1" />Deactivate
                    </button>
                    {isPlatformAdmin && (
                      <>
                        <select onChange={(e) => { if (e.target.value) handleBulkAction('change_role', e.target.value); e.target.value = ''; }} disabled={bulkActionLoading} className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-medium cursor-pointer border-none" data-testid="bulk-change-role">
                          <option value="">Change Role...</option>
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                        <select onChange={(e) => { if (e.target.value) handleBulkAction('change_tier', e.target.value); e.target.value = ''; }} disabled={bulkActionLoading} className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-medium cursor-pointer border-none" data-testid="bulk-change-tier">
                          <option value="">Change Tier...</option>
                          {TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <button onClick={() => handleBulkAction('delete')} disabled={bulkActionLoading} className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-xs font-medium transition-colors disabled:opacity-50" data-testid="bulk-delete-btn">
                          <Trash2 className="w-3.5 h-3.5 inline mr-1" />Delete
                        </button>
                      </>
                    )}
                    <button onClick={() => setSelectedUserIds(new Set())} className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-xs transition-colors" data-testid="bulk-clear-btn">
                      Clear
                    </button>
                  </div>
                  {bulkActionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                </div>
              )}

              {loading ? (
                <div className="text-center py-12 text-[#475569]">Loading...</div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
                  <table className="w-full min-w-[900px]">
                    <thead className="bg-[#F4F2EC]">
                      <tr>
                        <th className="px-4 py-4 text-left">
                          <input type="checkbox" checked={filteredUsers.length > 0 && selectedUserIds.size === filteredUsers.length} onChange={toggleSelectAll} className="w-4 h-4 rounded border-gray-300 text-[#1B3A6B] focus:ring-[#1B3A6B] cursor-pointer" data-testid="select-all-users" />
                        </th>
                        <th className="px-4 py-4 text-left text-sm font-medium text-[#0F1A2A]">User</th>
                        <th className="px-4 py-4 text-left text-sm font-medium text-[#0F1A2A]">Role</th>
                        <th className="px-4 py-4 text-left text-sm font-medium text-[#0F1A2A]">Tier</th>
                        <th className="px-4 py-4 text-left text-sm font-medium text-[#0F1A2A]">Status</th>
                        <th className="px-4 py-4 text-left text-sm font-medium text-[#0F1A2A]">Generations</th>
                        <th className="px-4 py-4 text-left text-sm font-medium text-[#0F1A2A]">Last Login</th>
                        <th className="px-4 py-4 text-left text-sm font-medium text-[#0F1A2A]">Joined</th>
                        <th className="px-4 py-4 text-right text-sm font-medium text-[#0F1A2A]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2DFD8]">
                      {filteredUsers.map(u => (
                        <tr key={u.id} className={`hover:bg-[#F9F8F6] ${selectedUserIds.has(u.id) ? 'bg-[#1B3A6B]/5' : ''}`}>
                          <td className="px-4 py-4">
                            <input type="checkbox" checked={selectedUserIds.has(u.id)} onChange={() => toggleSelectUser(u.id)} className="w-4 h-4 rounded border-gray-300 text-[#1B3A6B] focus:ring-[#1B3A6B] cursor-pointer" data-testid={`select-user-${u.id}`} />
                          </td>
                          <td className="px-4 py-4">
                            <div>
                              <div className="font-medium text-[#0F1A2A]">{u.name}</div>
                              <div className="text-sm text-[#475569]">{u.email}</div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${u.role === 'admin' ? 'bg-[#1B3A6B] text-white' : 'bg-[#F4F2EC] text-[#475569]'}`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            {isPlatformAdmin ? (
                              <select
                                value={u.subscription_tier || 'free'}
                                onChange={async (e) => {
                                  try {
                                    await axios.put(`${API}/admin/users/${u.id}/plan`, { tier: e.target.value });
                                    loadData();
                                  } catch (err) { alert('Failed to change plan'); }
                                }}
                                className={`px-2 py-1 rounded-lg text-xs font-medium border-none cursor-pointer ${
                                  u.subscription_tier === 'organization' ? 'bg-[#B8860B] text-white' :
                                  u.subscription_tier === 'church' ? 'bg-[#B8860B]/80 text-white' :
                                  u.subscription_tier === 'pro' ? 'bg-[#1B3A6B] text-white' :
                                  u.subscription_tier === 'starter' ? 'bg-[#254B8A] text-white' :
                                  'bg-[#F4F2EC] text-[#475569]'
                                }`}
                                data-testid={`plan-select-${u.id}`}
                              >
                                {TIERS.map(t => (
                                  <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                              </select>
                            ) : (
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                u.subscription_tier === 'organization' ? 'bg-[#B8860B] text-white' :
                                u.subscription_tier === 'church' ? 'bg-[#B8860B]/80 text-white' :
                                u.subscription_tier === 'pro' ? 'bg-[#1B3A6B] text-white' :
                                u.subscription_tier === 'starter' ? 'bg-[#254B8A] text-white' :
                                'bg-[#F4F2EC] text-[#475569]'
                              }`}>
                                {u.subscription_tier || 'free'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <span className={`flex items-center gap-1 text-sm ${u.is_active !== false ? 'text-green-600' : 'text-red-500'}`}>
                              {u.is_active !== false ? <><CheckCircle className="w-4 h-4" /> Active</> : <><Ban className="w-4 h-4" /> Disabled</>}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-[#475569]">{u.usage_stats?.generations || 0}</td>
                          <td className="px-4 py-4 text-sm text-[#475569]" data-testid={`last-login-${u.id}`}>
                            {u.last_login ? new Date(u.last_login).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : <span className="text-[#94A3B8] text-xs">Never</span>}
                          </td>
                          <td className="px-4 py-4 text-sm text-[#475569]">{new Date(u.created_at).toLocaleDateString()}</td>
                          <td className="px-4 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button onClick={() => openEditUser(u)} className="p-2 hover:bg-[#F4F2EC] rounded-lg" title="Edit user" data-testid={`edit-user-${u.id}`}>
                                <Edit3 className="w-4 h-4 text-[#1B3A6B]" />
                              </button>
                              <button onClick={() => toggleUserStatus(u.id, u.is_active !== false)} className="p-2 hover:bg-[#F4F2EC] rounded-lg" title={u.is_active !== false ? 'Disable user' : 'Enable user'}>
                                {u.is_active !== false ? <Ban className="w-4 h-4 text-[#475569]" /> : <CheckCircle className="w-4 h-4 text-green-600" />}
                              </button>
                              <button onClick={() => { setSelectedUser(u); setShowResetModal(true); }} className="p-2 hover:bg-[#F4F2EC] rounded-lg" title="Reset password">
                                <Key className="w-4 h-4 text-[#475569]" />
                              </button>
                              {u.role !== 'admin' && u.role !== 'platform_admin' && (
                                <button onClick={() => deleteUser(u.id)} className="p-2 hover:bg-red-50 rounded-lg" title="Delete user">
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </button>
                              )}
                              {isPlatformAdmin && u.role !== 'platform_admin' && (
                                <>
                                  <button onClick={() => handleImpersonate(u.id)} className="p-2 hover:bg-blue-50 rounded-lg" title="Impersonate user" data-testid={`impersonate-${u.id}`}>
                                    <Eye className="w-4 h-4 text-blue-600" />
                                  </button>
                                  <button onClick={() => { setDiscountUser(u); setShowDiscountModal(true); }} className="p-2 hover:bg-green-50 rounded-lg" title="Apply discount" data-testid={`discount-${u.id}`}>
                                    <Gift className="w-4 h-4 text-green-600" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* MODULES TAB */}
          {activeTab === 'modules' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-heading text-2xl font-bold text-[#1B3A6B]">Module Toggles</h2>
                  <p className="text-sm text-[#475569] mt-1">Enable or disable features globally across the platform.</p>
                </div>
                {moduleSaving && <Loader2 className="w-5 h-5 text-[#1B3A6B] animate-spin" />}
              </div>

              {loading ? (
                <div className="text-center py-12 text-[#475569]">Loading...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(moduleConfig).map(([moduleId, config]) => (
                    <div key={moduleId} className="bg-white rounded-xl p-5 shadow-sm flex items-center justify-between" data-testid={`module-toggle-${moduleId}`}>
                      <div>
                        <h4 className="font-medium text-[#0F1A2A]">{config.label}</h4>
                        <p className="text-xs text-[#475569] mt-0.5">{moduleId}</p>
                      </div>
                      <button
                        onClick={() => handleModuleToggle(moduleId)}
                        className="flex items-center"
                        data-testid={`module-switch-${moduleId}`}
                      >
                        {config.enabled ? (
                          <ToggleRight className="w-10 h-10 text-[#1B3A6B]" />
                        ) : (
                          <ToggleLeft className="w-10 h-10 text-[#475569]" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ANALYTICS TAB */}
          {activeTab === 'analytics' && analytics && (
            <div>
              <h2 className="font-heading text-2xl font-bold text-[#1B3A6B] mb-6">Platform Analytics</h2>
              
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
                <button onClick={() => setActiveTab('users')} className="bg-white p-6 rounded-xl shadow-sm text-left hover:shadow-md hover:ring-2 hover:ring-[#1B3A6B]/20 transition-all cursor-pointer" data-testid="widget-total-users">
                  <div className="text-3xl font-bold text-[#1B3A6B]">{analytics.users.total}</div>
                  <div className="text-sm text-[#475569] mt-1">Total Users</div>
                </button>
                <button onClick={() => setActiveTab('users')} className="bg-white p-6 rounded-xl shadow-sm text-left hover:shadow-md hover:ring-2 hover:ring-green-200 transition-all cursor-pointer" data-testid="widget-active-users">
                  <div className="text-3xl font-bold text-green-600">{analytics.users.active}</div>
                  <div className="text-sm text-[#475569] mt-1">Active Users</div>
                </button>
                <button onClick={() => setActiveTab('payments')} className="bg-white p-6 rounded-xl shadow-sm text-left hover:shadow-md hover:ring-2 hover:ring-[#B8860B]/20 transition-all cursor-pointer" data-testid="widget-generations">
                  <div className="text-3xl font-bold text-[#B8860B]">{analytics.usage.total_generations || 0}</div>
                  <div className="text-sm text-[#475569] mt-1">Total Generations</div>
                </button>
                <button onClick={() => setActiveTab('tickets')} className="bg-white p-6 rounded-xl shadow-sm text-left hover:shadow-md hover:ring-2 hover:ring-[#1B3A6B]/20 transition-all cursor-pointer" data-testid="widget-saved-studies">
                  <div className="text-3xl font-bold text-[#1B3A6B]">{analytics.content.total}</div>
                  <div className="text-sm text-[#475569] mt-1">Saved Studies</div>
                </button>
              </div>

              {/* LLM Budget Status - Platform Admin Only */}
              {isPlatformAdmin && analytics.llm_budget && (
                <div className={`mb-8 p-6 rounded-xl shadow-sm border-2 ${
                  analytics.llm_budget.status === 'exceeded' 
                    ? 'bg-red-50 border-red-300' 
                    : 'bg-emerald-50 border-emerald-200'
                }`} data-testid="llm-budget-widget">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        analytics.llm_budget.status === 'exceeded' ? 'bg-red-100' : 'bg-emerald-100'
                      }`}>
                        <CreditCard className={`w-5 h-5 ${
                          analytics.llm_budget.status === 'exceeded' ? 'text-red-600' : 'text-emerald-600'
                        }`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#0F1A2A]">AI Generation Budget</h3>
                        <p className={`text-sm ${
                          analytics.llm_budget.status === 'exceeded' ? 'text-red-600 font-medium' : 'text-emerald-600'
                        }`}>
                          {analytics.llm_budget.status === 'exceeded' ? 'Budget Exceeded - Generations Disabled' : 'Budget Active'}
                        </p>
                      </div>
                    </div>
                    <button 
                      onClick={handleCheckBudget} 
                      disabled={budgetChecking}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white border border-[#E2DFD8] hover:bg-[#F5F3F0] transition-colors disabled:opacity-50"
                      data-testid="check-budget-btn"
                    >
                      <RefreshCw className={`w-4 h-4 ${budgetChecking ? 'animate-spin' : ''}`} />
                      {budgetChecking ? 'Checking...' : 'Check Now'}
                    </button>
                  </div>
                  
                  {analytics.llm_budget.current_cost != null && analytics.llm_budget.max_budget != null && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-[#475569]">Usage</span>
                        <span className="font-medium text-[#0F1A2A]">
                          ${analytics.llm_budget.current_cost?.toFixed(2)} / ${analytics.llm_budget.max_budget?.toFixed(2)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className={`h-3 rounded-full transition-all ${
                            analytics.llm_budget.status === 'exceeded' ? 'bg-red-500' : 
                            (analytics.llm_budget.current_cost / analytics.llm_budget.max_budget) > 0.8 ? 'bg-amber-500' : 'bg-emerald-500'
                          }`}
                          style={{ width: `${Math.min(100, (analytics.llm_budget.current_cost / analytics.llm_budget.max_budget) * 100)}%` }}
                        />
                      </div>
                      {analytics.llm_budget.updated_at && (
                        <p className="text-xs text-[#94A3B8]">Last checked: {new Date(analytics.llm_budget.updated_at).toLocaleString()}</p>
                      )}
                    </div>
                  )}
                  
                  {analytics.llm_budget.status === 'exceeded' && (
                    <div className="mt-4 p-4 bg-white rounded-lg border border-red-200">
                      <p className="text-sm text-red-700 mb-3">
                        AI generations are currently disabled. To restore service, add balance to your Universal Key.
                      </p>
                      <a
                        href="https://emergentagent.com/profile"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#254B8A] transition-colors"
                        data-testid="add-balance-link"
                      >
                        <DollarSign className="w-4 h-4" />
                        Add Balance (Profile → Universal Key)
                      </a>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h3 className="font-semibold text-[#0F1A2A] mb-4">Content by Type</h3>
                  <div className="space-y-3">
                    {analytics.content.by_type.map(item => (
                      <div key={item._id} className="flex items-center justify-between">
                        <span className="text-[#475569] capitalize">{item._id || 'Unknown'}</span>
                        <span className="font-medium text-[#0F1A2A]">{item.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h3 className="font-semibold text-[#0F1A2A] mb-4">Usage Statistics</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[#475569]">Total Generations</span>
                      <span className="font-medium text-[#0F1A2A]">{analytics.usage.total_generations || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#475569]">Total Saves</span>
                      <span className="font-medium text-[#0F1A2A]">{analytics.usage.total_saves || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#475569]">Total Exports</span>
                      <span className="font-medium text-[#0F1A2A]">{analytics.usage.total_exports || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#475569]">Public Shared</span>
                      <span className="font-medium text-[#0F1A2A]">{analytics.content.public}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Revenue Section */}
              {revenueData && (
                <div className="mt-6">
                  <h3 className="font-semibold text-[#0F1A2A] mb-4 text-lg">Revenue & Subscriptions</h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white p-5 rounded-xl shadow-sm" data-testid="revenue-total">
                      <div className="text-2xl font-bold text-green-600">${(revenueData.total_revenue || 0).toFixed(2)}</div>
                      <div className="text-sm text-[#475569] mt-1">Total Revenue</div>
                    </div>
                    <div className="bg-white p-5 rounded-xl shadow-sm">
                      <div className="text-2xl font-bold text-[#1B3A6B]">{revenueData.total_transactions || 0}</div>
                      <div className="text-sm text-[#475569] mt-1">Transactions</div>
                    </div>
                    {revenueData.tier_distribution?.slice(0, 2).map(t => (
                      <div key={t.tier} className="bg-white p-5 rounded-xl shadow-sm">
                        <div className="text-2xl font-bold text-[#B8860B]">{t.count}</div>
                        <div className="text-sm text-[#475569] mt-1 capitalize">{t.tier} Users</div>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Tier Distribution */}
                    <div className="bg-white p-6 rounded-xl shadow-sm" data-testid="tier-distribution">
                      <h4 className="font-semibold text-[#0F1A2A] mb-4">Tier Distribution</h4>
                      <div className="space-y-3">
                        {revenueData.tier_distribution?.map(t => (
                          <div key={t.tier} className="flex items-center justify-between">
                            <span className="text-[#475569] capitalize">{t.tier}</span>
                            <div className="flex items-center gap-3">
                              <div className="w-24 h-2 bg-[#F4F2EC] rounded-full overflow-hidden">
                                <div className="h-full bg-[#1B3A6B] rounded-full" style={{ width: `${Math.min(100, (t.count / (analytics?.users?.total || 1)) * 100)}%` }} />
                              </div>
                              <span className="font-medium text-[#0F1A2A] w-8 text-right">{t.count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Organizations */}
                    <div className="bg-white p-6 rounded-xl shadow-sm" data-testid="org-grouping">
                      <h4 className="font-semibold text-[#0F1A2A] mb-4">Organizations</h4>
                      {revenueData.organizations?.length > 0 ? (
                        <div className="space-y-2">
                          {revenueData.organizations.map(o => (
                            <div key={o.org_id} className="flex items-center justify-between p-2 bg-[#F9F8F6] rounded-lg">
                              <div>
                                <span className="text-sm font-medium text-[#0F1A2A]">{o.admin_name}</span>
                                <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full ${
                                  o.tier === 'organization' ? 'bg-[#B8860B] text-white' : 'bg-[#F4F2EC] text-[#475569]'
                                }`}>{o.tier}</span>
                              </div>
                              <span className="text-sm text-[#475569]">{o.member_count} members</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-[#94A3B8] text-center py-4">No organizations yet</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PAYMENTS TAB — Platform Admin Only */}
          {activeTab === 'payments' && isPlatformAdmin && (
            <div>
              <h2 className="font-heading text-2xl font-bold text-[#1B3A6B] mb-6">Payments & Stripe</h2>
              {loading ? (
                <div className="text-center py-12 text-[#475569]">Loading...</div>
              ) : (
                <>
                  {/* Stripe MRR & Stats */}
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white p-5 rounded-xl shadow-sm" data-testid="stripe-mrr">
                      <div className="text-2xl font-bold text-green-600">${(payments?.stripe_mrr || 0).toFixed(2)}</div>
                      <div className="text-sm text-[#475569] mt-1">Monthly Recurring Revenue</div>
                    </div>
                    <div className="bg-white p-5 rounded-xl shadow-sm">
                      <div className="text-2xl font-bold text-[#1B3A6B]">{payments?.stripe_subscriptions?.length || 0}</div>
                      <div className="text-sm text-[#475569] mt-1">Stripe Subscriptions</div>
                    </div>
                    <div className="bg-white p-5 rounded-xl shadow-sm">
                      <div className="text-2xl font-bold text-[#B8860B]">{payments?.stripe_charges?.length || 0}</div>
                      <div className="text-sm text-[#475569] mt-1">Recent Charges</div>
                    </div>
                  </div>

                  {/* Stripe Subscriptions */}
                  {payments?.stripe_subscriptions?.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm overflow-x-auto mb-6" data-testid="stripe-subscriptions">
                      <div className="px-6 py-4 border-b border-[#E2DFD8]">
                        <h3 className="font-semibold text-[#0F1A2A]">Stripe Subscriptions</h3>
                      </div>
                      <table className="w-full min-w-[600px]">
                        <thead className="bg-[#F4F2EC]">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Customer</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Plan</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Period End</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E2DFD8]">
                          {payments.stripe_subscriptions.map(s => (
                            <tr key={s.id} className="hover:bg-[#F9F8F6]">
                              <td className="px-6 py-3 text-sm text-[#0F1A2A] font-mono text-xs">{s.customer}</td>
                              <td className="px-6 py-3 text-sm text-[#475569]">{s.plan_name || '-'}</td>
                              <td className="px-6 py-3 text-sm font-medium text-[#0F1A2A]">${s.plan_amount?.toFixed(2)}/mo</td>
                              <td className="px-6 py-3">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.status === 'active' ? 'bg-green-100 text-green-800' : s.status === 'trialing' ? 'bg-blue-100 text-blue-800' : s.status === 'canceled' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>{s.status}</span>
                              </td>
                              <td className="px-6 py-3 text-sm text-[#475569]">{s.current_period_end ? new Date(s.current_period_end).toLocaleDateString() : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Stripe Charges */}
                  {payments?.stripe_charges?.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm overflow-x-auto mb-6" data-testid="stripe-charges">
                      <div className="px-6 py-4 border-b border-[#E2DFD8]">
                        <h3 className="font-semibold text-[#0F1A2A]">Recent Stripe Charges</h3>
                      </div>
                      <table className="w-full min-w-[600px]">
                        <thead className="bg-[#F4F2EC]">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Description</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E2DFD8]">
                          {payments.stripe_charges.map(c => (
                            <tr key={c.id} className="hover:bg-[#F9F8F6]">
                              <td className="px-6 py-3 text-sm text-[#0F1A2A]">{c.receipt_email || c.customer}</td>
                              <td className="px-6 py-3 text-sm font-medium text-[#0F1A2A]">${c.amount?.toFixed(2)} {c.currency?.toUpperCase()}</td>
                              <td className="px-6 py-3">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.status === 'succeeded' ? 'bg-green-100 text-green-800' : c.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>{c.status}</span>
                              </td>
                              <td className="px-6 py-3 text-sm text-[#475569] truncate max-w-[200px]">{c.description || '-'}</td>
                              <td className="px-6 py-3 text-sm text-[#475569]">{new Date(c.created).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Local Transactions */}
                  {payments?.payments?.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
                      <div className="px-6 py-4 border-b border-[#E2DFD8]">
                        <h3 className="font-semibold text-[#0F1A2A]">Local Transaction Log</h3>
                      </div>
                      <table className="w-full min-w-[600px]">
                        <thead className="bg-[#F4F2EC]">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">User</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Tier</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E2DFD8]">
                          {payments.payments.map((p, i) => (
                            <tr key={i} className="hover:bg-[#F9F8F6]">
                              <td className="px-6 py-3 text-sm text-[#0F1A2A]">{p.user_email}</td>
                              <td className="px-6 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#1B3A6B] text-white capitalize">{p.tier}</span></td>
                              <td className="px-6 py-3 text-sm font-medium text-[#0F1A2A]">${p.amount?.toFixed(2)}</td>
                              <td className="px-6 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.payment_status === 'paid' ? 'bg-green-100 text-green-800' : p.payment_status === 'initiated' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-800'}`}>{p.payment_status}</span></td>
                              <td className="px-6 py-3 text-sm text-[#475569]">{new Date(p.created_at).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {(!payments?.payments?.length && !payments?.stripe_charges?.length && !payments?.stripe_subscriptions?.length) && (
                    <div className="text-center py-12 text-[#475569]">No payment data found</div>
                  )}
                </>
              )}
            </div>
          )}

          {/* TICKETS TAB */}
          {activeTab === 'tickets' && (
            <AdminHelpdesk adminEmail={user?.email} />
          )}

          {/* CONTENT / CMS TAB */}
          {activeTab === 'content' && (
            <div>
              <h2 className="font-heading text-2xl font-bold text-[#1B3A6B] mb-6">Content Management</h2>
              
              {/* About Page Image */}
              <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
                <h3 className="font-semibold text-[#0F1A2A] mb-4 flex items-center gap-2"><Image className="w-5 h-5 text-[#B8860B]" /> About Page Image</h3>
                <div className="flex gap-3">
                  <input type="text" value={aboutImageUrl} onChange={(e) => setAboutImageUrl(e.target.value)} className="flex-1 px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]" placeholder="Enter image URL for About page..." data-testid="about-image-url-input" />
                  <button onClick={handleSaveAboutImage} disabled={cmsSaving} className="btn-primary px-5 py-3 rounded-lg font-medium flex items-center gap-2 disabled:opacity-50" data-testid="save-about-image">
                    {cmsSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                  </button>
                </div>
                {aboutImageUrl && <img src={aboutImageUrl} alt="Preview" className="mt-3 max-h-48 rounded-lg object-cover" />}
              </div>

              {/* Knowledge Articles */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-[#0F1A2A] flex items-center gap-2"><FileText className="w-5 h-5 text-[#B8860B]" /> Knowledge Articles</h3>
                  <button onClick={() => setShowArticleForm(true)} className="btn-primary py-2 px-4 rounded-lg font-medium flex items-center gap-2 text-sm" data-testid="add-article-btn">
                    <FileText className="w-4 h-4" /> New Article
                  </button>
                </div>
                {articles.length === 0 ? (
                  <p className="text-[#475569] text-sm py-4">No articles yet.</p>
                ) : (
                  <div className="space-y-2">
                    {articles.map(a => (
                      <div key={a.id} className="flex items-center justify-between p-3 bg-[#F9F8F6] rounded-lg">
                        <div>
                          <h4 className="font-medium text-[#0F1A2A] text-sm">{a.title}</h4>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-[#475569]">{a.category}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${a.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>{a.published ? 'Published' : 'Draft'}</span>
                          </div>
                        </div>
                        <button onClick={() => handleDeleteArticle(a.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Guide Page Videos */}
              <div className="bg-white rounded-xl p-6 shadow-sm mt-6" data-testid="guide-video-manager">
                <h3 className="font-semibold text-[#0F1A2A] mb-4 flex items-center gap-2"><Play className="w-5 h-5 text-[#B8860B]" /> Guide Page Videos</h3>
                <p className="text-sm text-[#475569] mb-4">Add tutorial videos to the Guide page. Supports YouTube, Vimeo, and direct video URLs.</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                  <input value={newGuideVideoTitle} onChange={e => setNewGuideVideoTitle(e.target.value)} placeholder="Video title" className="px-3 py-2.5 text-sm border border-[#E2DFD8] rounded-lg" data-testid="admin-guide-video-title" />
                  <select value={newGuideVideoSection} onChange={e => setNewGuideVideoSection(e.target.value)} className="px-3 py-2.5 text-sm border border-[#E2DFD8] rounded-lg" data-testid="admin-guide-video-section">
                    <option value="">Select section...</option>
                    <option value="getting-started">Getting Started</option>
                    <option value="generators">AI Generators</option>
                    <option value="tools">Ministry Tools</option>
                    <option value="managing">Managing Content</option>
                    <option value="advanced">Advanced Features</option>
                  </select>
                </div>
                <input value={newGuideVideoUrl} onChange={e => setNewGuideVideoUrl(e.target.value)} placeholder="Video URL (YouTube, Vimeo, or direct link)" className="w-full px-3 py-2.5 text-sm border border-[#E2DFD8] rounded-lg mb-2" data-testid="admin-guide-video-url" />
                <input value={newGuideVideoDesc} onChange={e => setNewGuideVideoDesc(e.target.value)} placeholder="Description (optional)" className="w-full px-3 py-2.5 text-sm border border-[#E2DFD8] rounded-lg mb-2" data-testid="admin-guide-video-desc" />
                <button
                  onClick={async () => {
                    if (!newGuideVideoTitle.trim() || !newGuideVideoUrl.trim()) return;
                    try {
                      const res = await axios.post(`${API}/admin/guide/video`, { title: newGuideVideoTitle, url: newGuideVideoUrl, section_id: newGuideVideoSection, description: newGuideVideoDesc });
                      setGuideVideos([...guideVideos, res.data.video]);
                      setNewGuideVideoTitle(''); setNewGuideVideoUrl(''); setNewGuideVideoSection(''); setNewGuideVideoDesc('');
                    } catch (err) { alert(err.response?.data?.detail || 'Failed to add video'); }
                  }}
                  disabled={!newGuideVideoTitle.trim() || !newGuideVideoUrl.trim()}
                  className="px-4 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-[#254B8A] mb-4"
                  data-testid="admin-add-guide-video-btn"
                >
                  Add Video
                </button>

                {guideVideos.length === 0 ? (
                  <p className="text-[#94A3B8] text-sm py-3">No videos added yet.</p>
                ) : (
                  <div className="space-y-2">
                    {guideVideos.map(v => (
                      <div key={v.id} className="flex items-center justify-between p-3 bg-[#F9F8F6] rounded-lg">
                        <div>
                          <h4 className="font-medium text-[#0F1A2A] text-sm">{v.title}</h4>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-[#475569]">{v.section_id || 'No section'}</span>
                            <span className="text-xs text-[#94A3B8] truncate max-w-[200px]">{v.url}</span>
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            try { await axios.delete(`${API}/admin/guide/video/${v.id}`); setGuideVideos(guideVideos.filter(g => g.id !== v.id)); }
                            catch { alert('Failed to remove'); }
                          }}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Custom Plan Requests */}
              {planRequests.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm mt-6" data-testid="plan-requests-section">
                  <h3 className="font-semibold text-[#0F1A2A] mb-4 flex items-center gap-2"><FileText className="w-5 h-5 text-[#B8860B]" /> Custom Plan Requests ({planRequests.length})</h3>
                  <div className="space-y-3">
                    {planRequests.map(r => (
                      <div key={r.id} className="p-4 bg-[#F9F8F6] rounded-lg border border-[#E2DFD8]">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-[#0F1A2A]">{r.name} — {r.org_name || 'No org'}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === 'pending' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>{r.status}</span>
                        </div>
                        <p className="text-sm text-[#475569]">{r.email} | Size: {r.org_size || 'N/A'}</p>
                        {r.message && <p className="text-sm text-[#475569] mt-1 italic">"{r.message}"</p>}
                        <p className="text-xs text-[#94A3B8] mt-1">{new Date(r.created_at).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {activeTab === 'pricing' && isPlatformAdmin && (
            <div>
              <h2 className="font-heading text-2xl font-bold text-[#1B3A6B] mb-6">Pricing Editor</h2>
              <p className="text-sm text-[#475569] mb-6">Modify subscription tier pricing. Changes apply immediately.</p>
              
              {loading ? (
                <div className="text-center py-12 text-[#475569]">Loading...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(pricingTiers).filter(([k]) => k !== 'free').map(([tierKey, tier]) => (
                    <div key={tierKey} className="bg-white rounded-xl p-6 shadow-sm" data-testid={`pricing-edit-${tierKey}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-[#0F1A2A] text-lg">{tier.name}</h3>
                        <span className="text-xs px-2 py-1 bg-[#1B3A6B]/10 text-[#1B3A6B] rounded-full">{tierKey}</span>
                      </div>
                      
                      {editingTier === tierKey ? (
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-[#0F1A2A]">$</span>
                          <input type="number" step="0.01" value={tierEditPrice} onChange={(e) => setTierEditPrice(e.target.value)} className="flex-1 px-3 py-2 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]" data-testid={`price-input-${tierKey}`} />
                          <button onClick={() => handleSavePricing(tierKey)} className="btn-primary px-4 py-2 rounded-lg text-sm">Save</button>
                          <button onClick={() => setEditingTier(null)} className="btn-secondary px-4 py-2 rounded-lg text-sm">Cancel</button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="text-3xl font-bold text-[#1B3A6B]">${tier.price?.toFixed(2)}<span className="text-sm text-[#475569] font-normal">/mo</span></span>
                          <button onClick={() => { setEditingTier(tierKey); setTierEditPrice(tier.price?.toString()); }} className="text-sm text-[#1B3A6B] font-medium hover:underline" data-testid={`edit-price-${tierKey}`}>Edit</button>
                        </div>
                      )}
                      
                      <div className="mt-3 text-xs text-[#475569]">
                        {tier.generations_per_month === -1 ? 'Unlimited' : tier.generations_per_month} generations/mo
                        {tier.max_team_members > 0 && <span className="ml-2">| {tier.max_team_members} team seats</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* PROMO CODES TAB (Platform Admin) */}
          {activeTab === 'promos' && isPlatformAdmin && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl font-bold text-[#1B3A6B]">Promo Codes</h2>
                <button onClick={() => setShowPromoForm(true)} className="btn-primary py-2 px-4 rounded-lg font-medium flex items-center gap-2 text-sm" data-testid="create-promo-btn">
                  <Tag className="w-4 h-4" /> New Promo
                </button>
              </div>
              
              {loading ? (
                <div className="text-center py-12 text-[#475569]">Loading...</div>
              ) : promos.length === 0 ? (
                <div className="text-center py-12 text-[#475569]">No promo codes yet</div>
              ) : (
                <div className="space-y-3">
                  {promos.map(p => (
                    <div key={p.id} className="bg-white rounded-xl p-5 shadow-sm flex items-center justify-between" data-testid={`promo-${p.code}`}>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono font-bold text-[#1B3A6B] text-lg">{p.code}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${p.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>{p.active ? 'Active' : 'Inactive'}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-[#475569]">
                          <span>
                            {p.discount_type === 'percentage' ? `${p.discount_value}% off` : 
                             p.discount_type === 'fixed' ? `$${p.discount_value} off` : 
                             `${p.discount_value} extra trial days`}
                          </span>
                          <span>Used: {p.times_used}/{p.max_uses === -1 ? 'Unlimited' : p.max_uses}</span>
                          {p.expires_at && <span>Expires: {new Date(p.expires_at).toLocaleDateString()}</span>}
                        </div>
                      </div>
                      <button onClick={() => handleDeletePromo(p.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* AI BUDGET TAB - Platform Admin */}
          {activeTab === 'budget' && isPlatformAdmin && (
            <div>
              <h2 className="font-heading text-2xl font-bold text-[#1B3A6B] mb-6">AI Generation Budget</h2>
              
              {loading ? (
                <div className="text-center py-12 text-[#475569]">Loading...</div>
              ) : (
                <div className="space-y-6">
                  {/* Budget Status Card */}
                  <div className={`p-8 rounded-2xl border-2 ${
                    budgetData?.status === 'exceeded' 
                      ? 'bg-red-50 border-red-300' 
                      : 'bg-emerald-50 border-emerald-200'
                  }`} data-testid="budget-status-card">
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                        budgetData?.status === 'exceeded' ? 'bg-red-100' : 'bg-emerald-100'
                      }`}>
                        <CreditCard className={`w-7 h-7 ${
                          budgetData?.status === 'exceeded' ? 'text-red-600' : 'text-emerald-600'
                        }`} />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-[#0F1A2A]">
                          {budgetData?.status === 'exceeded' ? 'Budget Exceeded' : 'Budget Active'}
                        </h3>
                        <p className={`text-sm ${
                          budgetData?.status === 'exceeded' ? 'text-red-600' : 'text-emerald-600'
                        }`}>
                          {budgetData?.status === 'exceeded' 
                            ? 'All AI generation is currently paused' 
                            : 'AI generation is operational'}
                        </p>
                      </div>
                    </div>
                    
                    {budgetData?.current_cost != null && budgetData?.max_budget != null && (
                      <div className="space-y-3 mb-6">
                        <div className="flex justify-between text-sm">
                          <span className="text-[#475569] font-medium">Current Usage</span>
                          <span className="font-bold text-[#0F1A2A] text-lg">
                            ${budgetData.current_cost?.toFixed(2)} / ${budgetData.max_budget?.toFixed(2)}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-4">
                          <div 
                            className={`h-4 rounded-full transition-all ${
                              budgetData.status === 'exceeded' ? 'bg-red-500' : 
                              (budgetData.current_cost / budgetData.max_budget) > 0.8 ? 'bg-amber-500' : 'bg-emerald-500'
                            }`}
                            style={{ width: `${Math.min(100, (budgetData.current_cost / budgetData.max_budget) * 100)}%` }}
                          />
                        </div>
                        <p className="text-xs text-[#94A3B8]">
                          {((budgetData.current_cost / budgetData.max_budget) * 100).toFixed(1)}% of budget used
                        </p>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <button
                        onClick={handleCheckBudget}
                        disabled={budgetChecking}
                        className="flex items-center gap-2 px-5 py-2.5 bg-white border border-[#E2DFD8] rounded-lg text-sm font-medium hover:bg-[#F5F3F0] transition-colors disabled:opacity-50"
                        data-testid="budget-refresh-btn"
                      >
                        <RefreshCw className={`w-4 h-4 ${budgetChecking ? 'animate-spin' : ''}`} />
                        {budgetChecking ? 'Checking...' : 'Refresh Status'}
                      </button>
                      <a
                        href="https://emergentagent.com/profile"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-5 py-2.5 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#254B8A] transition-colors"
                        data-testid="budget-add-balance-btn"
                      >
                        <DollarSign className="w-4 h-4" />
                        Add Balance
                      </a>
                    </div>
                  </div>

                  {/* How It Works */}
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <h3 className="font-semibold text-[#0F1A2A] mb-4">How AI Budget Works</h3>
                    <div className="space-y-3 text-sm text-[#475569]">
                      <p>Your AI generations are powered by the Emergent Universal Key. Each generation consumes a small amount of budget.</p>
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-[#1B3A6B]/10 text-[#1B3A6B] rounded-full flex items-center justify-center text-xs font-bold">1</span>
                        <p>Go to your <strong>Emergent Profile</strong> page</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-[#1B3A6B]/10 text-[#1B3A6B] rounded-full flex items-center justify-center text-xs font-bold">2</span>
                        <p>Navigate to <strong>Universal Key</strong> section</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-[#1B3A6B]/10 text-[#1B3A6B] rounded-full flex items-center justify-center text-xs font-bold">3</span>
                        <p>Click <strong>Add Balance</strong> to top up. Enable <strong>Auto Top-Up</strong> to avoid interruptions.</p>
                      </div>
                    </div>
                  </div>

                  {budgetData?.updated_at && (
                    <p className="text-xs text-[#94A3B8] text-center">
                      Last updated: {new Date(budgetData.updated_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* CRM TAB — Platform Admin */}
          {activeTab === 'crm' && isPlatformAdmin && (
            <CRMPanel />
          )}

          {/* NEWSLETTER TAB — Platform Admin */}
          {activeTab === 'newsletter' && isPlatformAdmin && (
            <NewsletterPanel />
          )}

          {/* SETTINGS TAB — Platform Admin */}
          {activeTab === 'settings' && isPlatformAdmin && (
            <div data-testid="admin-settings">
              <h2 className="font-heading text-2xl font-bold text-[#1B3A6B] mb-6">API Settings</h2>
              <p className="text-sm text-[#475569] mb-6">Manage API keys and configuration. Changes apply immediately without redeployment.</p>

              <div className="space-y-4 max-w-2xl">
                {/* Resend API Key */}
                <div className="bg-white rounded-xl p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <label className="font-medium text-[#0F1A2A] text-sm">Resend API Key</label>
                    {platformSettings.RESEND_API_KEY?.is_set && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-800">
                        {platformSettings.RESEND_API_KEY.source}
                      </span>
                    )}
                  </div>
                  <input
                    value={settingsForm.RESEND_API_KEY || ''}
                    onChange={e => setSettingsForm(p => ({...p, RESEND_API_KEY: e.target.value}))}
                    placeholder="re_xxxxxxxx..."
                    className="w-full px-3 py-2.5 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] text-sm font-mono"
                    data-testid="setting-resend-key"
                  />
                  <p className="text-xs text-[#94A3B8] mt-1">Get your key at resend.com/api-keys</p>
                </div>

                {/* Sender Email */}
                <div className="bg-white rounded-xl p-5 shadow-sm">
                  <label className="font-medium text-[#0F1A2A] text-sm block mb-2">Sender Email</label>
                  <input
                    value={settingsForm.SENDER_EMAIL || ''}
                    onChange={e => setSettingsForm(p => ({...p, SENDER_EMAIL: e.target.value}))}
                    placeholder="info@yourdomain.com"
                    className="w-full px-3 py-2.5 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] text-sm"
                    data-testid="setting-sender-email"
                  />
                  <p className="text-xs text-[#94A3B8] mt-1">Must match a verified domain in Resend</p>
                </div>

                {/* Sender Display Name */}
                <div className="bg-white rounded-xl p-5 shadow-sm">
                  <label className="font-medium text-[#0F1A2A] text-sm block mb-2">Sender Display Name</label>
                  <input
                    value={settingsForm.SENDER_DISPLAY_NAME || ''}
                    onChange={e => setSettingsForm(p => ({...p, SENDER_DISPLAY_NAME: e.target.value}))}
                    placeholder="e.g. Bible Tools"
                    className="w-full px-3 py-2.5 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] text-sm"
                    data-testid="setting-display-name"
                  />
                  <p className="text-xs text-[#94A3B8] mt-1">Recipients will see this name instead of the email address (e.g. "Bible Tools" &lt;info@dsashun.com&gt;)</p>
                </div>

                {/* Emergent LLM Key */}
                <div className="bg-white rounded-xl p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <label className="font-medium text-[#0F1A2A] text-sm">Emergent LLM Key (Universal Key)</label>
                    {platformSettings.EMERGENT_LLM_KEY?.is_set && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-800">
                        {platformSettings.EMERGENT_LLM_KEY.source}
                      </span>
                    )}
                  </div>
                  <input
                    value={settingsForm.EMERGENT_LLM_KEY || ''}
                    onChange={e => setSettingsForm(p => ({...p, EMERGENT_LLM_KEY: e.target.value}))}
                    placeholder="ek_xxxxxxxx..."
                    className="w-full px-3 py-2.5 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] text-sm font-mono"
                    data-testid="setting-llm-key"
                  />
                  <p className="text-xs text-[#94A3B8] mt-1">Powers all AI generation. Manage balance at Profile → Universal Key</p>
                </div>

                {/* Stripe API Key */}
                <div className="bg-white rounded-xl p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <label className="font-medium text-[#0F1A2A] text-sm">Stripe API Key</label>
                    {platformSettings.STRIPE_API_KEY?.is_set && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-800">
                        {platformSettings.STRIPE_API_KEY.source}
                      </span>
                    )}
                  </div>
                  <input
                    value={settingsForm.STRIPE_API_KEY || ''}
                    onChange={e => setSettingsForm(p => ({...p, STRIPE_API_KEY: e.target.value}))}
                    placeholder="sk_live_xxxxxxxx..."
                    className="w-full px-3 py-2.5 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] text-sm font-mono"
                    data-testid="setting-stripe-key"
                  />
                  <p className="text-xs text-[#94A3B8] mt-1">For payment processing. Get at dashboard.stripe.com/apikeys</p>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={async () => {
                      setSettingsSaving(true);
                      setSettingsTestResult(null);
                      try {
                        const res = await axios.put(`${API}/admin/settings`, settingsForm);
                        setSettingsTestResult({ type: 'success', message: res.data.message });
                        loadData();
                      } catch (err) {
                        setSettingsTestResult({ type: 'error', message: err.response?.data?.detail || 'Failed to save' });
                      } finally { setSettingsSaving(false); }
                    }}
                    disabled={settingsSaving}
                    className="flex-1 py-3 bg-[#1B3A6B] text-white rounded-lg font-medium text-sm disabled:opacity-50 hover:bg-[#254B8A]"
                    data-testid="settings-save"
                  >
                    {settingsSaving ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}
                    Save Settings
                  </button>
                  <button
                    onClick={async () => {
                      setSettingsTestResult(null);
                      try {
                        const res = await axios.post(`${API}/admin/settings/test-email`);
                        setSettingsTestResult({ type: 'success', message: `Test email sent to ${res.data.to} from ${res.data.from}` });
                      } catch (err) {
                        setSettingsTestResult({ type: 'error', message: err.response?.data?.detail || 'Test failed' });
                      }
                    }}
                    className="px-6 py-3 bg-white border border-[#E2DFD8] text-[#475569] rounded-lg font-medium text-sm hover:bg-[#F4F2EC]"
                    data-testid="settings-test-email"
                  >
                    <Send className="w-4 h-4 inline mr-2" />Test Email
                  </button>
                </div>

                {/* Result message */}
                {settingsTestResult && (
                  <div className={`p-3 rounded-lg text-sm ${settingsTestResult.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`} data-testid="settings-result">
                    {settingsTestResult.message}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="add-user-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-semibold text-[#1B3A6B]">Add New User</h3>
              <button onClick={() => { setShowAddUser(false); setAddUserError(''); }}>
                <X className="w-5 h-5 text-[#475569]" />
              </button>
            </div>
            
            {addUserError && (
              <div className="bg-red-50 text-red-700 rounded-lg p-3 mb-4 text-sm">{addUserError}</div>
            )}
            
            <div className="space-y-3">
              <input type="text" value={addUserForm.name} onChange={(e) => setAddUserForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]" data-testid="add-user-name" />
              <input type="email" value={addUserForm.email} onChange={(e) => setAddUserForm(p => ({ ...p, email: e.target.value }))} placeholder="Email address" className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]" data-testid="add-user-email" />
              <input type="password" value={addUserForm.password} onChange={(e) => setAddUserForm(p => ({ ...p, password: e.target.value }))} placeholder="Password (min 6 chars)" className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]" data-testid="add-user-password" />
              <div className="grid grid-cols-2 gap-3">
                <select value={addUserForm.role} onChange={(e) => setAddUserForm(p => ({ ...p, role: e.target.value }))} className="px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]" data-testid="add-user-role">
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
                <select value={addUserForm.subscription_tier} onChange={(e) => setAddUserForm(p => ({ ...p, subscription_tier: e.target.value }))} className="px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]" data-testid="add-user-tier">
                  {TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
            
            <div className="flex gap-3 mt-5">
              <button onClick={() => { setShowAddUser(false); setAddUserError(''); }} className="flex-1 btn-secondary py-2.5 rounded-lg font-medium">Cancel</button>
              <button onClick={handleAddUser} disabled={addUserLoading} className="flex-1 btn-primary py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50" data-testid="confirm-add-user">
                {addUserLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Create User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-semibold text-[#1B3A6B]">Reset Password</h3>
              <button onClick={() => { setShowResetModal(false); setNewPassword(''); }}>
                <X className="w-5 h-5 text-[#475569]" />
              </button>
            </div>
            <p className="text-[#475569] mb-4">Reset password for {selectedUser.email}</p>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password (min 6 characters)" className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none mb-4 focus:ring-2 focus:ring-[#1B3A6B]" data-testid="admin-new-password-input" />
            <div className="flex gap-3">
              <button onClick={() => { setShowResetModal(false); setNewPassword(''); }} className="flex-1 btn-secondary py-2.5 rounded-lg font-medium">Cancel</button>
              <button onClick={resetPassword} className="flex-1 btn-primary py-2.5 rounded-lg font-medium" data-testid="admin-confirm-reset">Reset Password</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUser && editUserId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={e => e.target === e.currentTarget && setShowEditUser(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl" data-testid="edit-user-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-semibold text-[#1B3A6B]">Edit User</h3>
              <button onClick={() => setShowEditUser(false)}><X className="w-5 h-5 text-[#475569]" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#94A3B8] mb-1 block">Name</label>
                  <input value={editUserForm.name} onChange={e => setEditUserForm(p => ({...p, name: e.target.value}))} className="w-full px-3 py-2.5 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] text-sm" data-testid="edit-user-name" />
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] mb-1 block">Email</label>
                  <input value={editUserForm.email} onChange={e => setEditUserForm(p => ({...p, email: e.target.value}))} className="w-full px-3 py-2.5 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] text-sm" data-testid="edit-user-email" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Church / Organization</label>
                <input value={editUserForm.church_name} onChange={e => setEditUserForm(p => ({...p, church_name: e.target.value}))} className="w-full px-3 py-2.5 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] text-sm" data-testid="edit-user-church" />
              </div>
              {isPlatformAdmin && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#94A3B8] mb-1 block">Role</label>
                    <select value={editUserForm.role} onChange={e => setEditUserForm(p => ({...p, role: e.target.value}))} className="w-full px-3 py-2.5 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] text-sm" data-testid="edit-user-role">
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                      <option value="platform_admin">Platform Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-[#94A3B8] mb-1 block">Subscription Tier</label>
                    <select value={editUserForm.subscription_tier} onChange={e => setEditUserForm(p => ({...p, subscription_tier: e.target.value}))} className="w-full px-3 py-2.5 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] text-sm" data-testid="edit-user-tier">
                      <option value="free">Free</option>
                      <option value="starter">Starter</option>
                      <option value="pro">Pro</option>
                      <option value="church">Church</option>
                      <option value="organization">Organization</option>
                    </select>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <label className="text-xs text-[#94A3B8]">Account Status:</label>
                <button onClick={() => setEditUserForm(p => ({...p, is_active: !p.is_active}))} className={`px-3 py-1 rounded-full text-xs font-medium ${editUserForm.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-testid="edit-user-status">
                  {editUserForm.is_active ? 'Active' : 'Disabled'}
                </button>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowEditUser(false)} className="flex-1 btn-secondary py-2.5 rounded-lg font-medium">Cancel</button>
                <button onClick={handleEditUser} className="flex-1 btn-primary py-2.5 rounded-lg font-medium" data-testid="edit-user-save">Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Response Modal */}
      {selectedTicket && !showResetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="ticket-response-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-semibold text-[#1B3A6B]">Respond to Ticket</h3>
              <button onClick={() => { setSelectedTicket(null); setTicketResponse(''); }}>
                <X className="w-5 h-5 text-[#475569]" />
              </button>
            </div>
            <div className="bg-[#F9F8F6] rounded-lg p-4 mb-4">
              <p className="font-medium text-[#0F1A2A] text-sm">{selectedTicket.subject}</p>
              <p className="text-sm text-[#475569] mt-1">From: {selectedTicket.name} ({selectedTicket.email})</p>
            </div>
            <textarea
              value={ticketResponse}
              onChange={(e) => setTicketResponse(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none mb-4 focus:ring-2 focus:ring-[#1B3A6B] resize-none"
              placeholder="Type your response..."
              data-testid="ticket-response-input"
            />
            <div className="flex gap-3">
              <button onClick={() => { setSelectedTicket(null); setTicketResponse(''); }} className="flex-1 btn-secondary py-2.5 rounded-lg font-medium">Cancel</button>
              <button onClick={handleTicketResponse} className="flex-1 btn-primary py-2.5 rounded-lg font-medium flex items-center justify-center gap-2" data-testid="send-ticket-response">
                <Send className="w-4 h-4" /> Send Response
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Article Form Modal */}
      {showArticleForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="article-form-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-semibold text-[#1B3A6B]">New Article</h3>
              <button onClick={() => setShowArticleForm(false)}><X className="w-5 h-5 text-[#475569]" /></button>
            </div>
            <div className="space-y-3">
              <input type="text" value={articleForm.title} onChange={(e) => setArticleForm(p => ({ ...p, title: e.target.value }))} placeholder="Article title" className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]" data-testid="article-title" />
              <select value={articleForm.category} onChange={(e) => setArticleForm(p => ({ ...p, category: e.target.value }))} className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]">
                <option value="general">General</option>
                <option value="bible-study">Bible Study</option>
                <option value="devotional">Devotional</option>
                <option value="ministry">Ministry</option>
                <option value="tutorial">Tutorial</option>
              </select>
              <textarea value={articleForm.content} onChange={(e) => setArticleForm(p => ({ ...p, content: e.target.value }))} rows={6} placeholder="Article content..." className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] resize-none" data-testid="article-content" />
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={articleForm.published} onChange={(e) => setArticleForm(p => ({ ...p, published: e.target.checked }))} />
                <span className="text-sm text-[#0F1A2A]">Published</span>
              </label>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowArticleForm(false)} className="flex-1 btn-secondary py-2.5 rounded-lg font-medium">Cancel</button>
              <button onClick={handleCreateArticle} className="flex-1 btn-primary py-2.5 rounded-lg font-medium" data-testid="save-article">Create Article</button>
            </div>
          </div>
        </div>
      )}

      {/* Promo Code Form Modal */}
      {showPromoForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="promo-form-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-semibold text-[#1B3A6B]">Create Promo Code</h3>
              <button onClick={() => setShowPromoForm(false)}><X className="w-5 h-5 text-[#475569]" /></button>
            </div>
            <div className="space-y-3">
              <input type="text" value={promoForm.code} onChange={(e) => setPromoForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="Code (e.g., WELCOME20)" className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] font-mono" data-testid="promo-code-input" />
              <select value={promoForm.discount_type} onChange={(e) => setPromoForm(p => ({ ...p, discount_type: e.target.value }))} className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]" data-testid="promo-type">
                <option value="percentage">Percentage Off (%)</option>
                <option value="fixed">Fixed Amount Off ($)</option>
                <option value="trial_extension">Trial Extension (Days)</option>
              </select>
              <input type="number" value={promoForm.discount_value} onChange={(e) => setPromoForm(p => ({ ...p, discount_value: e.target.value }))} placeholder={promoForm.discount_type === 'percentage' ? 'Discount %' : promoForm.discount_type === 'fixed' ? 'Amount $' : 'Extra days'} className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]" data-testid="promo-value" />
              <div className="grid grid-cols-2 gap-3">
                <input type="number" value={promoForm.max_uses === -1 ? '' : promoForm.max_uses} onChange={(e) => setPromoForm(p => ({ ...p, max_uses: e.target.value || -1 }))} placeholder="Max uses (empty=unlimited)" className="px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]" />
                <input type="date" value={promoForm.expires_at} onChange={(e) => setPromoForm(p => ({ ...p, expires_at: e.target.value }))} className="px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]" />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowPromoForm(false)} className="flex-1 btn-secondary py-2.5 rounded-lg font-medium">Cancel</button>
              <button onClick={handleCreatePromo} className="flex-1 btn-primary py-2.5 rounded-lg font-medium" data-testid="save-promo">Create Promo</button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Discount Modal */}
      {showDiscountModal && discountUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="discount-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-semibold text-[#1B3A6B]">Apply Discount</h3>
              <button onClick={() => { setShowDiscountModal(false); setDiscountUser(null); }}><X className="w-5 h-5 text-[#475569]" /></button>
            </div>
            <p className="text-sm text-[#475569] mb-4">Applying discount to: <strong>{discountUser.email}</strong></p>
            <div className="space-y-3">
              <select value={discountForm.discount_type} onChange={(e) => setDiscountForm(p => ({ ...p, discount_type: e.target.value }))} className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]">
                <option value="percentage">Percentage Off (%)</option>
                <option value="fixed">Fixed Amount Off ($)</option>
                <option value="trial_extension">Trial Extension (Days)</option>
              </select>
              <input type="number" value={discountForm.discount_value} onChange={(e) => setDiscountForm(p => ({ ...p, discount_value: e.target.value }))} placeholder={discountForm.discount_type === 'trial_extension' ? 'Extra days' : 'Discount value'} className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]" data-testid="discount-value" />
              <input type="text" value={discountForm.reason} onChange={(e) => setDiscountForm(p => ({ ...p, reason: e.target.value }))} placeholder="Reason (optional)" className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]" />
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => { setShowDiscountModal(false); setDiscountUser(null); }} className="flex-1 btn-secondary py-2.5 rounded-lg font-medium">Cancel</button>
              <button onClick={handleApplyDiscount} className="flex-1 btn-primary py-2.5 rounded-lg font-medium" data-testid="apply-discount-btn">Apply Discount</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
